import { useId, useState } from "react";
import { sendContactForm } from "@/services/contactService";
import { getRecaptchaToken } from "@/services/recaptcha";
import { useLocale } from "@/i18n/useLocale";
import "./QuickLead.css";

/**
 * Captura de baja friccion: solo el email.
 *
 * Convive con el wizard de 5 pasos en vez de reemplazarlo. El wizard califica
 * bien, pero como UNICA puerta pide cinco pasos antes de poder decir "hola".
 * Este atajo captura al que no los va a completar hoy, y no canibaliza: el que
 * quiere calificarse igual usa el formulario largo.
 *
 * Reusa el mismo endpoint que el wizard: el backend exige service y message, y
 * se los mandamos marcados como lead rapido para que en el mail se distinga de
 * una consulta completa.
 */
function QuickLead() {
  const { t } = useLocale();
  const emailId = useId();

  const recaptchaSiteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
  const [email, setEmail] = useState("");
  const [state, setState] = useState<"idle" | "sending" | "done">("idle");
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const value = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      setError(t.quickLead.errInvalid);
      return;
    }

    setError(null);
    setState("sending");

    if (!recaptchaSiteKey) {
      setState("idle");
      setError(t.wizard.errRecaptchaKey);
      return;
    }

    // Sin token el backend rechaza el envio: recaptcha_token es obligatorio.
    let token: string;
    try {
      token = await getRecaptchaToken(recaptchaSiteKey, {
        load: t.wizard.errRecaptchaLoad,
        unavailable: t.wizard.errRecaptchaUnavailable,
      });
    } catch {
      setState("idle");
      setError(t.wizard.errRecaptchaVerify);
      return;
    }

    // El backend valida name, phone y country como obligatorios. Aca solo hay
    // email, asi que se completan con un marcador explicito en vez de dejarlos
    // vacios: quien lea el mail tiene que ver de una que es un lead rapido y no
    // una consulta a la que le faltan datos.
    const marker = "[Lead rápido — solo email]";
    const result = await sendContactForm({
      name: marker,
      email: value,
      phone: marker,
      country: marker,
      company: "",
      service: "Lead rápido",
      message:
        "Dejó su email desde el bloque de contacto rápido de la home. No completó el formulario largo.",
      recaptcha_token: token,
    });

    if (result.success) {
      setState("done");
    } else {
      setState("idle");
      setError(result.message);
    }
  };

  if (state === "done") {
    return (
      <div className="quick-lead quick-lead--done">
        {/* role=status: el lector de pantalla anuncia el exito sin robar el
            foco, que es lo que pasaria con role=alert. */}
        <p className="quick-lead__success" role="status">
          {t.quickLead.success}
        </p>
      </div>
    );
  }

  return (
    <div className="quick-lead">
      <h3 className="quick-lead__title">{t.quickLead.title}</h3>
      <p className="quick-lead__description">{t.quickLead.description}</p>

      <form className="quick-lead__form" onSubmit={handleSubmit} noValidate>
        {/* Label visible y no solo placeholder: el placeholder desaparece al
            escribir y deja al usuario sin saber que campo esta completando. */}
        <label className="quick-lead__label" htmlFor={emailId}>
          Email
        </label>

        <div className="quick-lead__row">
          <input
            id={emailId}
            type="email"
            inputMode="email"
            autoComplete="email"
            className={`quick-lead__input ${error ? "has-error" : ""}`}
            placeholder={t.quickLead.placeholder}
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (error) setError(null);
            }}
            aria-invalid={error ? "true" : undefined}
            aria-describedby={error ? `${emailId}-error` : undefined}
            disabled={state === "sending"}
          />

          <button
            type="submit"
            className="quick-lead__submit"
            disabled={state === "sending"}
          >
            {state === "sending" ? t.quickLead.sending : t.quickLead.submit}
          </button>
        </div>

        {error && (
          <p className="quick-lead__error" id={`${emailId}-error`} role="alert">
            {error}
          </p>
        )}
      </form>
    </div>
  );
}

export default QuickLead;
